import axios from 'axios';
import { toastNotifyError } from '@/components/Common/Toast';
import { ENDPOINT } from 'src/config/endpoints';
import { getAdditionalPage } from '../../../../src/services';
import { getWidgetType } from '../utils/common';
import { ADMIN_WIDGETS } from 'src/constants/widgets';
import { StoreDetails } from 'src/redux/reducers';
import { NEW_SUBSCRIPTIONS_STATUS } from 'src/constants/subscription';
import store from 'src/redux/store';
import { getStoreSubscriptionConditions } from 'src/utils/storeSubscriptionConfigs';
import { TypeWidgetsByRoute } from 'src/context/WidgetDndContext/types';
import { BUILDER_WIDGET_ROUTES } from 'src/constants/builderConstants';
import { COMMON_LAYOUT_TYPE } from 'src/components/DesignSystem/Widgets2.0/ListPresentationV1/components/Testimonials/Constants';

const isProdEnv = () => {
  return (
    process.env.NEXT_PUBLIC_CUSTOM_ENV === 'DEV' ||
    process.env.NEXT_PUBLIC_CUSTOM_ENV === 'STAGE-1' ||
    process.env.NEXT_PUBLIC_CUSTOM_ENV === 'STAGE-2'
  );
};

interface ISaveWidgetDnDData {
  storeInfo: StoreDetails;
  widgetsByRoute: TypeWidgetsByRoute;
  authToken: string | string[];
  jsonWidgetMetadata?: any;
  forDraft?: boolean;
}

function checkIfNewConfigApplied(style) {
  if (style?.sortAndFilter?.layout_desktop !== COMMON_LAYOUT_TYPE.ZERO) {
    return true;
  }
  return false;
}

export const saveWidgetDnDData = ({
  storeInfo,
  widgetsByRoute,
  authToken,
  jsonWidgetMetadata,
  forDraft,
}: ISaveWidgetDnDData) => {
  const subscriptionStatus = getStoreSubscriptionConditions();
  const storeId = storeInfo.store_id;
  const storeThemeId = storeInfo.theme.store_theme_id;
  const shouldReplaceWidgets =
    !storeInfo?.theme?.brand_website_flag &&
    ((!subscriptionStatus.isOldSubscriptionActive && !forDraft) ||
      (forDraft && jsonWidgetMetadata?.status === 1));

  const config = {
    headers: {
      auth_token: authToken,
      app_os: 'cfe',
      app_version: '0.1.0',
      'Content-Type': 'application/json',
    },
  };

  const widgetConfigs = Object.entries(widgetsByRoute);

  const apiCall = widgetConfigs.map(([route, item]) => {
    const widgetId = item.jsonPageWidgetSchema.widget_id;
    const plpData = storeInfo?.theme?.components?.PLP || [];
    const plpWidget = plpData[0];
    if (
      route === BUILDER_WIDGET_ROUTES.COLLECTION &&
      checkIfNewConfigApplied(item.style) &&
      !(
        plpData?.length === 1 &&
        plpWidget?.name === 'SortingAndFilterWidget1' &&
        plpWidget?.sub_type === 'SortingAndFilterWidget'
      )
    ) {
      const data = {
        store_id: storeId,
        store_theme_id: storeThemeId,
        widgets: [
          {
            type: 16,
            component_type: 'PLP',
            ordering: 0,
            name: 'SortingAndFilterWidget1',
            widget_template_id: '',
            status: 1,
            template: '',
          },
        ],
      };
      axios.post(ENDPOINT.STORE.addThemeWidget, data, config);
    }
    if (widgetId && !shouldReplaceWidgets) {
      const data = {
        store_id: storeId,
        widget: {
          id: widgetId,
          status: forDraft ? 2 : 1,
          template: JSON.stringify(item.widgets),
        },
      };

      return axios.put(ENDPOINT.STORE.updateThemeWidget, data, config);
    } else {
      const data = {
        store_id: storeId,
        store_theme_id: storeThemeId,
        widgets: [
          {
            component_type: route === BUILDER_WIDGET_ROUTES.HOME ? 'Body' : route,
            status: forDraft ? 2 : 1,
            type: 18,
            ordering: 4,
            ...(route === BUILDER_WIDGET_ROUTES.HOME
              ? {
                  widget_template_id: isProdEnv()
                    ? '63db40fb30ceabf0796737cf'
                    : '63e0fe18c0d21e0a530c29fe',
                }
              : {}),
            template: JSON.stringify(item.widgets),
          },
        ],
      };
      return axios.post(ENDPOINT.STORE.addThemeWidget, data, config);
    }
  });

  return Promise.all(apiCall);
};

export const getJSONPageWidgetSchema = async ({
  forRoute: pageType,
  storeID,
  widget_call_status,
}) => {
  const storeId = storeID || store.getState().storeReducer.store.store_id;
  const response = await axios.get(
    ENDPOINT.ADDITIONAL_PAGES.getJsonPageWidgetSchema(
      storeId,
      pageType,
      widget_call_status
    )
  );
  const widgetResponse = response.data;
  return widgetResponse;
  // return JSON.parse(widgetJsonString);
};

export const getWidgetData = async (widgetId: string) => {
  const widgetResponse = axios.get(
    ENDPOINT.ADDITIONAL_PAGES.getStaticWidgetDetails(widgetId)
  );
  const widgetJsonString = await widgetResponse.then((response) => response.data.data);
  return JSON.parse(widgetJsonString);
};

export const getAdditionalPageData = async (slug: string, storeId: number, pageType: number = 1) => {
  const additionalPageResponse = await getAdditionalPage(slug, storeId, false, undefined, pageType);

  if (additionalPageResponse?.data?.status) {
    return additionalPageResponse.data.page || null;
  }
  return;
};

interface ISaveNewWidgetDndHeaderFooter {
  storeInfo: StoreDetails;
  widgetProps: Record<string, any>;
  widgetType: string;
  authToken: string | string[];
  forDraft?: boolean;
}

export const saveNewWidgetDnDHeaderFooter = ({
  storeInfo,
  widgetProps,
  widgetType,
  authToken,
  forDraft,
}: ISaveNewWidgetDndHeaderFooter) => {
  const storeId = storeInfo?.store_id;
  const storeThemeId = storeInfo?.theme?.store_theme_id;
  const isStoreWithNewSubscription =
    storeInfo?.new_subscription_status === NEW_SUBSCRIPTIONS_STATUS.ACTIVE;
  const widget = store.getState().jsonWidgetsReducer.themeComponents?.[widgetType]?.[0];
  const shouldReplaceWidgets =
    (isStoreWithNewSubscription && !forDraft) || (forDraft && widget?.status === 1);

  const config = {
    headers: {
      auth_token: authToken,
      app_os: 'cfe',
      app_version: '0.1.0',
      'Content-Type': 'application/json',
    },
  };

  const widgetId = widget?.widget_id || widgetProps.widget_id;

  // Case of Widget Updation
  if (widgetId && !shouldReplaceWidgets) {
    const data = {
      store_id: storeId,
      widget: {
        component_type: widgetType,
        ordering: 0,
        name:
          widgetType === 'Footer' ? ADMIN_WIDGETS.FOOTER.name : ADMIN_WIDGETS.HEADER.name,
        id: widgetId,
        type: getWidgetType(widgetProps?.sub_type),
        config: widgetProps,
        sub_type: widgetProps?.sub_type,
        status: forDraft ? 2 : 1,
      },
    };
    return axios.put(ENDPOINT.STORE.updateThemeWidget, data, config);
  } else {
    // Case of widget addition - in case of header footer although this wouldn't be called as when theme gets activated, we get a header footer by default. But still keeping this code as fallback.
    const data = {
      store_id: storeId,
      store_theme_id: storeThemeId,
      widgets: [
        {
          type: widgetType === 'Footer' ? 6 : 5,
          component_type: widgetType,
          ordering: 0,
          name:
            widgetType === 'Footer'
              ? ADMIN_WIDGETS.FOOTER.name
              : ADMIN_WIDGETS.HEADER.name,
          config: {
            ...widgetProps,
          },
          status: forDraft ? 2 : 1,
        },
      ],
    };
    return axios.post(ENDPOINT.STORE.addThemeWidget, data, config);
  }
};

export const saveWidgetDnDHeaderFooter = (
  storeInfo: StoreDetails,
  widgetProps: Record<string, any>,
  widgetType: string,
  authToken: string | string[],
  forDraft: boolean
) => {
  const storeId = storeInfo?.store_id;
  const storeThemeId = storeInfo?.theme?.store_theme_id;
  const isStoreWithNewSubscription =
    storeInfo?.new_subscription_status === NEW_SUBSCRIPTIONS_STATUS.ACTIVE;
  const widget = store.getState().jsonWidgetsReducer.themeComponents?.[widgetType]?.[0];
  const shouldReplaceWidgets =
    (isStoreWithNewSubscription && !forDraft) || (forDraft && widget?.status === 1);

  const config = {
    headers: {
      auth_token: authToken,
      app_os: 'cfe',
      app_version: '0.1.0',
      'Content-Type': 'application/json',
    },
  };

  const widgetId = widget?.widget_id || widgetProps.widget_id;
  // Case of Widget Updation
  if (widgetId && !shouldReplaceWidgets) {
    const data = {
      store_id: storeId,
      widget: {
        ...widgetProps,
        id: widgetId,
        type: getWidgetType(widgetProps?.sub_type),
        status: forDraft ? 2 : 1,
      },
    };
    return axios.put(ENDPOINT.STORE.updateThemeWidget, data, config);
  } else {
    // Case of widget addition - in case of header footer although this wouldn't be called as when theme gets activated, we get a header footer by default. But still keeping this code as fallback.
    const data = {
      store_id: storeId,
      store_theme_id: storeThemeId,
      widgets: [
        {
          type: widgetType === 'Footer' ? 6 : 5,
          component_type: widgetType,
          ordering: 0,
          name: widgetProps?.name,
          status: forDraft ? 2 : 1,
          config: {
            ...widgetProps,
          },
        },
      ],
    };
    return axios.post(ENDPOINT.STORE.addThemeWidget, data, config);
  }
};

export const uploadMediaToS3Handler = async ({
  payload,
  authToken,
  callback,
  type,
}: any) => {
  try {
    const forVideo = type === 'video';
    const config = {
      headers: {
        auth_token: authToken,
        app_os: 'cfe',
        app_version: '0.1.0',
        'Content-Type': !forVideo ? 'application/json' : 'multipart/form-data',
      },
    };
    const response = await axios.post(
      ENDPOINT.LOGS[!forVideo ? 'UPLOAD_IMAGE_TO_S3' : 'UPLOAD_MEDIA_TO_S3'],
      payload,
      config
    );
    if (response && response?.data?.status) {
      const imageURL = JSON.parse(response?.data?.data);
      callback && callback(imageURL);
      return imageURL;
    } else {
      toastNotifyError(response?.data?.message || 'Something went wrong!');
      callback && callback({ error: true });
      return { error: true, errorMessage: response?.data?.message || '' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || '';
    callback && callback({ error: true, errorMessage });
    return { error: true, errorMessage };
  }
};
